import { formatDate } from '@/helpers/DateHelpers';
import * as Dates from '@/constants/Dates';
import * as registrationConsts from '@/constants/Registration';
import { getDeliveryPrice } from '@/helpers/QueryHelpers';
import { formatCouponCode } from '@/helpers/CouponHelpers';
import IncompleteRegistrationService from '@/services/users/incompleteRegistrationService/IncompleteRegistrationService';
import ProductsService from '@/services/logistics/ProductsService.js';

const getDefaultState = () => {
    return {
        sections: {
            [registrationConsts.SECTION_ACCOUNT]: false,
            [registrationConsts.SECTION_DELIVERY]: false,
            [registrationConsts.SECTION_PAYMENT]: false,
        },
        details: {
            incompleteRegistrantUuid: null,
            welcomeName: null,
            account: {
                firstName: null,
                lastName: null,
                email: null,
                password: null,
                optInToMarketing: false,
                optOutToMarketing: false,
                optInToThirdPartyMarketing: false,
                telephone: null,
                weeklyEmail: false,
            },
            billingAddress: {
                addressLine1: null,
                addressLine2: null,
                city: null,
                country: process.env.VUE_APP_COUNTRY_ISO,
                postcode: null,
            },
            deliveryAddress: {
                addressLine1: null,
                addressLine2: null,
                city: null,
                country: process.env.VUE_APP_COUNTRY_ISO,
                postcode: null,
            },
            couponCode: null,
            friendCode: null,
            couponProperties: null,
            deliveryAddressIsBilling: true,
            deliveryComment: null,
            propertyType: null,
            card: {},
            firstPossibleDeliveryDate: null,
            firstDeliveryDateDesired: null,
            courierName: null,
            AddedAddonItem: false,
        },
        plans: [
            {
                plan_name: 'Weekly',
                plan_interval: 'weekly',
                // pre_heading: 'Most popular',
                icon: 'weekly-deliveries.png',
                display: true,
            },
            {
                plan_name: 'Fortnightly',
                plan_interval: 'fortnightly',
                icon: 'fortnightly-deliveries.png',
                display: true,
            },
            // {
            //     plan_name: 'Every 3 weeks',
            //     plan_interval: 'three-weekly',
            //     icon: 'fortnightly-deliveries.png',
            //     display: false,
            // },
            // {
            //     plan_name: 'Monthly (Every 4 weeks)',
            //     plan_interval: 'monthly',
            //     icon: 'fortnightly-deliveries.png',
            //     display: false,
            // },
        ],
        primaryProducts: [
            {
                name: 'Flower Box - A',
                price: Number(process.env.VUE_APP_BOX_PRICE),
                product_family: 'Standard',
            },
            {
                name: 'Flower Box - BBA',
                price: Number(process.env.VUE_APP_BUMPER_BOX_PRICE),
                product_family: 'Bumper',
            },
        ],
        propertyTypes: [
            {
                name: 'House',
                icon: 'house',
            },
            {
                name: 'Flat',
                icon: 'flat',
            },
            {
                name: 'Office',
                icon: 'office',
            },
        ],
        preferredDeliveryDay: null,
        selectedPlan: null,
        selectedProductID: null,
        selectedProductName: null,
        defaultProductFamily: 'standard',
        selectedProductDetails: {
            icon: 'Standard-icon.png',
            image: 'Standard-image.jpg',
            name: 'Flower Box - A',
            display_name: 'Classic box',
            plan_name: 'Classic',
            post_heading: 'For small to medium spaces',
            pre_heading: '',
            price: 25,
            product_family: 'Standard',
        }, // for new checkout flow.
        boxPrice: Number(process.env.VUE_APP_BOX_PRICE),
        bumperBoxPrice: Number(process.env.VUE_APP_BUMPER_BOX_PRICE),
        deliveryPrice: Number(process.env.VUE_APP_DELIVERY_PRICE || 0),
        region: null,
        source: null,
        canvasser: null,
        checkoutPrice: 0,
        hasFetchedProducts: false,
        orderSummary: null,
        dislikeLily: false,
    };
};

export default {
    namespaced: true,
    state: getDefaultState(),
    mutations: {
        resetState(state) {
            Object.assign(state, getDefaultState());
        },
        setOrderSummary: (state, payload) => state.orderSummary = payload,
        setWelcomeName: (state, name) => state.details.welcomeName = name,
        setFirstName: (state, name) => state.details.account.firstName = name,
        setLastName: (state, name) => state.details.account.lastName = name,
        setEmail: (state, email) => state.details.account.email = email,
        setPassword: (state, password) => state.details.account.password = password,
        setOptInMarketing: (state, value) => state.details.account.optInToMarketing = value,
        setOptInThirdPartyMarketing: (state, value) => state.details.account.optInToThirdPartyMarketing = value,
        setSectionComplete: (state, section) => state.sections[section] = true,
        setDeliveryAddress: (state, address) => {
            state.details.deliveryAddress = Object.assign(state.details.deliveryAddress, address);
        },
        setBillingAddress: (state, address) => {
            state.details.billingAddress = Object.assign(state.details.billingAddress, address);
        },
        setFirstPossibleDeliveryDate: (state, date) => state.details.firstPossibleDeliveryDate = date,
        setCourierName: (state, name) => state.details.courierName = name,
        setFirstDeliveryDateDesired: (state, date) => state.details.firstDeliveryDateDesired = date,
        setDeliveryComment: (state, comment) => state.details.deliveryComment = comment,
        setPropertyType: (state, type) => state.details.propertyType = type,
        setTelephone: (state, telephone) => state.details.account.telephone = telephone,
        setDeliveryAddressIsBilling: (state, bool) => state.details.deliveryAddressIsBilling = bool,
        setCouponCode: (state, code) => state.details.couponCode = code,
        setFriendCode: (state, code) => state.details.friendCode = code,
        setCouponProperties: (state, code) => state.details.couponProperties = code,
        setIncompleteRegistrantUuid: (state, uuid) => state.details.incompleteRegistrantUuid = uuid,
        setPreferredDeliveryDay: (state, value) => state.preferredDeliveryDay = value,
        setSelectedPlan: (state, value) => state.selectedPlan = value,
        setSelectedProductID: (state, value) => state.selectedProductID = value,
        setSelectedProductName: (state, value) => state.selectedProductName = value,
        // setShowNewCheckout: (state, value) => state.newCheckout = value,
        setSelectedProductDetails: (state, value) => state.selectedProductDetails = value,
        setRegionID: (state, value) => state.region = value,
        setSourceID: (state, value) => state.source = value,
        setCanvasserID: (state, value) => state.canvasser = value,
        setCheckoutPrice: (state, value) => state.checkoutPrice = value,
        setPropertyTypes: (state, value) => state.primaryProducts = value,
        setHasFetchedProducts: (state, value) => state.hasFetchedProducts = value,
        setAddedAddonItem: (state, bool) => state.details.AddedAddonItem = bool,
        setWeeklyEmail: (state, value) => state.details.account.weeklyEmail = value,
        setDislikeLily: (state, value) => state.dislikeLily = value,
    },
    actions: {
        async fetchIncompleteRegistrationData({ commit, state }, payload) {
            let service = new IncompleteRegistrationService();
            let response = await service.getIncompleteRegistrationUserData(payload);
            let data = response.data.properties;
            commit('setFirstName', data.first_name);
            commit('setLastName', data.last_name);
            commit('setEmail', data.email);
            commit('setPassword', data.password);
            commit('setOptInMarketing', data.opt_in);
            commit('setOptInThirdPartyMarketing', data.third_party);
            commit('setRegionID', data.region_id);
            commit('setSourceID', data.source_id);
            commit('setCanvasserID', data.canvasser_id);
            commit('setDeliveryAddress', data.delivery_address);
            commit('setCourierName', data.courier_name);
            commit('setDeliveryComment', data.delivery_comment);
            commit('setPropertyType', data.property_type);
            commit('setTelephone', data.telephone);
            commit('setPreferredDeliveryDay', data.first_possible_delivery_date);
            commit('setSectionComplete', registrationConsts.SECTION_ACCOUNT);
            commit('setSectionComplete', registrationConsts.SECTION_DELIVERY);
            commit('setFriendCode', formatCouponCode(data.referral_code));
            commit('setCouponCode', formatCouponCode(data.referral_code));
        },
        getAllProducts({ commit, state }) {
            let service = new ProductsService();
            service.getAllProducts()
                .then((response) => {
                    commit('setPropertyTypes', response.data.products);
                    commit('setHasFetchedProducts', true);
                })
                .catch((err) => console.log(err.message));
        },
    },
    getters: {
        getWelcomeName: state => state.details.welcomeName,
        getFirstName: state => state.details.account.firstName,
        getLastName: state => state.details.account.lastName,
        getFullName: state => {
            return `${state.details.account.firstName} ${state.details.account.lastName}`;
        },
        getEmail: state => state.details.account.email,
        getPassword: state => state.details.account.password,
        getOptInMarketing: state => state.details.account.optInToMarketing,
        getOptInThirdPartyMarketing: state => state.details.account.optInToThirdPartyMarketing,
        getDeliveryAddress: state => state.details.deliveryAddress,
        getBillingAddress: state => state.details.billingAddress,
        getFirstPossibleDeliveryDate: state => state.details.firstPossibleDeliveryDate,
        getCourierName: state => state.details.courierName,
        getFirstDeliveryDay: state => {
            return state.details.firstPossibleDeliveryDate ? formatDate(state.details.firstPossibleDeliveryDate, Dates.FULL_DAY) : null;
        },
        getFirstDeliveryDateDesired: state => state.details.firstDeliveryDateDesired,
        getDeliveryComment: state => state.details.deliveryComment,
        getPropertyType: state => state.details.propertyType,
        getTelephone: state => state.details.account.telephone,
        getDeliveryAddressIsBilling: state => state.details.deliveryAddressIsBilling,
        getCouponCode: state => state.details.couponCode,
        getFriendCode: state => state.details.friendCode,
        getCouponProperties: state => state.details.couponProperties,
        getIncompleteRegistrantUuid: state => state.details.incompleteRegistrantUuid,
        getSectionCompleted: state => section => state.sections[section],
        getPreferredDeliveryDay: state => state.preferredDeliveryDay,
        getSelectedPlan: state => state.selectedPlan || state.plans[1],
        getSelectedProductID: state => state.selectedProductID,
        getDefaultProductFamily: state => state.defaultProductFamily,
        getSelectedProductName: state => state.selectedProductName,
        getAllPlans: state => state.plans,
        getBoxPrice: state => state.boxPrice,
        getCheckoutPrice: state => state.checkoutPrice,
        getBumperBoxPrice: state => state.bumperBoxPrice,
        getSelectedProductDetails: state => state.selectedProductDetails,
        getPrimaryProducts: state => state.primaryProducts,
        getPropertyTypes: state => state.propertyTypes,
        getDeliveryPrice: () => getDeliveryPrice(),
        getRegionId: (state) => state.region,
        getSourceId: (state) => state.source,
        getCanvasserId: (state) => state.canvasser ? parseInt(state.canvasser) : null,
        getHasFetchedProducts: (state) => state.hasFetchedProducts,
        getAddedAddonItem: (state) => state.details.AddedAddonItem,
        getOrderSummary: (state) => state.orderSummary,
        getWeeklyEmail: state => state.details.account.weeklyEmail,
        getDislikeLily: state => state.dislikeLily,
    },
};
